import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayScrollLock } from './useOverlayScrollLock';
import { X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type PromptSheetProps = {
    icon: LucideIcon;
    tone?: 'primary' | 'whatsapp' | 'warning';
    title: string;
    description?: ReactNode;
    onClose: () => void;
    children?: ReactNode;
};

const iconTones = {
    primary: 'bg-primary/10 text-primary',
    whatsapp: 'bg-[#25D366]/15 text-[#128C4B] dark:text-[#25D366]',
    warning: 'bg-warning/15 text-warning',
};

// Avisos abertos juntos (ex.: WhatsApp e notificacoes no primeiro acesso) aparecem um por vez.
let queue: symbol[] = [];
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const usePromptTurn = () => {
    const token = useRef(Symbol('prompt'));
    const [active, setActive] = useState(false);

    useEffect(() => {
        const id = token.current;
        const update = () => setActive(queue[0] === id);
        queue.push(id);
        listeners.add(update);
        notify();
        return () => {
            queue = queue.filter((item) => item !== id);
            listeners.delete(update);
            notify();
        };
    }, []);

    return active;
};

// Aviso curto: folha inferior no celular e dialogo pequeno no desktop. Para telas com muito conteudo, usar Modal.
export const PromptSheet = (props: PromptSheetProps) => {
    const active = usePromptTurn();
    return active ? <PromptSheetContent {...props} /> : null;
};

const PromptSheetContent = ({ icon: Icon, tone = 'primary', title, description, onClose, children }: PromptSheetProps) => {
    const [visible, setVisible] = useState(false);
    const [viewport, setViewport] = useState<{ height: number; top: number } | null>(null);
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const closeRef = useRef(onClose);
    closeRef.current = onClose;

    useOverlayScrollLock(scrollRef);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => setVisible(true));
        panelRef.current?.focus({ preventScroll: true });
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') closeRef.current();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.cancelAnimationFrame(frame);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    // Acompanha o teclado do celular, para o campo e o botao nao ficarem escondidos atras dele.
    useEffect(() => {
        const visual = window.visualViewport;
        if (!visual) return;
        const update = () => setViewport({ height: visual.height, top: visual.offsetTop });
        update();
        visual.addEventListener('resize', update);
        visual.addEventListener('scroll', update);
        return () => {
            visual.removeEventListener('resize', update);
            visual.removeEventListener('scroll', update);
        };
    }, []);

    const root = document.getElementById('outside-root');
    if (!root) return null;

    return createPortal(
        <div
            className={`fixed inset-x-0 top-0 z-[1000] flex h-[100dvh] items-end justify-center bg-black/40 transition-opacity duration-200 sm:items-center sm:p-6 ${visible ? 'opacity-100' : 'opacity-0'}`}
            style={viewport ? { height: viewport.height, top: viewport.top } : undefined}
            onClick={onClose}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
                className={`relative flex max-h-[92%] w-full flex-col overflow-hidden rounded-t-[1.75rem] bg-base-100 shadow-2xl outline-none transition-transform duration-300 ease-out motion-reduce:transition-none sm:max-w-[26rem] sm:rounded-[1.75rem] ${visible ? 'translate-y-0' : 'translate-y-8'}`}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fechar"
                    className="absolute right-3 top-3 z-[1] flex h-8 w-8 items-center justify-center rounded-full bg-base-content/[0.07] text-sm text-base-content/60 transition-colors hover:bg-base-content/[0.12]"
                >
                    <X aria-hidden="true" />
                </button>
                <div
                    ref={scrollRef}
                    data-overlay-scroll
                    className="min-h-0 overflow-y-auto overscroll-contain px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-7"
                >
                    <div className="text-center">
                        <span className={`mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-2xl ${iconTones[tone]}`} aria-hidden="true">
                            <Icon aria-hidden="true" />
                        </span>
                        <h2 id={titleId} className="mt-4 text-xl font-semibold tracking-tight text-base-content">{title}</h2>
                        {description && <div className="mt-1.5 text-[0.9375rem] leading-relaxed text-base-content/65">{description}</div>}
                    </div>
                    {children && <div className="mt-5">{children}</div>}
                </div>
            </div>
        </div>,
        root,
    );
};

export const PromptBenefits = ({ items }: { items: { icon: LucideIcon; text: string }[] }) => (
    <ul className="space-y-3 rounded-2xl bg-base-200 px-4 py-3.5 text-sm text-base-content/80 dark:bg-base-300">
        {items.map((item) => (
            <li key={item.text} className="flex items-center gap-3">
                <item.icon className="w-4 shrink-0 text-base-content/45" aria-hidden="true" />
                <span>{item.text}</span>
            </li>
        ))}
    </ul>
);
